import { useMemo, useState, useCallback, useEffect } from 'react';
import { getSelectedState, GridSelectionChangeEvent, GridHeaderSelectionChangeEvent } from '@progress/kendo-react-grid';
import { CompositeFilterDescriptor, getter } from '@progress/kendo-data-query';
import { useAppSelector } from '@/hooks/customHooks';

export interface ISelectedItem {
  [id: string]: boolean | number[];
}

const useGridSelection = () => {
  const initialFilter: CompositeFilterDescriptor = {
    logic: 'and',
    filters: [],
  };
  const gridDataRState = useAppSelector((state) => state.preferences.gridDataState);
  const SELECTED_FIELD = 'selected';
  const DATA_ITEM_KEY = 'primaryKey';
  const [selectedState, setSelectedState] = useState<ISelectedItem>({});
  const [dataGridFilter, setFilter] = useState(initialFilter as CompositeFilterDescriptor);
  const idGetter = getter(DATA_ITEM_KEY);

  useEffect(() => {
    const { filter } = gridDataRState;

    if (filter && filter.filters && !filter.filters.length) {
      setFilter(initialFilter);
    }
  }, [gridDataRState]);

  const reduceNewSelectedItems = (selectedState: ISelectedItem): ISelectedItem => {
    return Object.keys(selectedState).reduce((prev, curr) => {
      return selectedState[curr] ? { ...prev, [curr]: selectedState[curr] } : prev;
    }, {});
  };

  const nrOfSelectedItems = useMemo(() => Object.keys(selectedState).length, [selectedState]);

  const onSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      const newSelectedState = getSelectedState({
        event,
        selectedState,
        dataItemKey: DATA_ITEM_KEY,
      });
      setSelectedState(reduceNewSelectedItems(newSelectedState));
    },
    [selectedState],
  );

  const getSelectedPks = (): number[] => {
    return Object.keys(selectedState).map((item) => parseInt(item));
  };

  const onHeaderSelectionChange = useCallback((event: GridHeaderSelectionChangeEvent) => {
    const checkboxElement = event.syntheticEvent.target as HTMLInputElement;
    const { checked } = checkboxElement;
    const newSelectedState: ISelectedItem = {};

    event.dataItems.forEach((item) => {
      newSelectedState[idGetter(item)] = checked;
    });

    setSelectedState(reduceNewSelectedItems(newSelectedState));
  }, []);

  return {
    SELECTED_FIELD,
    DATA_ITEM_KEY,
    selectedState,
    setSelectedState,
    nrOfSelectedItems,
    onSelectionChange,
    getSelectedPks,
    onHeaderSelectionChange,
    idGetter,
    dataGridFilter,
    setFilter,
    initialFilter,
  };
};

export default useGridSelection;
